import { useParams, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Helmet } from 'react-helmet-async';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { ArrowRight, Loader2 } from 'lucide-react';
import DOMPurify from 'dompurify';

const SeoPage = () => {
  const { slug } = useParams();

  const { data: page, isLoading } = useQuery({
    queryKey: ['seo-page', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('seo_pages')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'published')
        .single();
      
      if (error) throw error;
      
      // Increment impressions
      await supabase
        .from('seo_pages')
        .update({ impressions: (data.impressions || 0) + 1 })
        .eq('id', data.id);
      
      return data;
    },
    enabled: !!slug,
  });

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  if (!page) return <Navigate to="/404" replace />;

  return (
    <>
      <Helmet>
        <title>{page.title}</title>
        <meta name="description" content={page.meta_description} />
        {page.keywords && <meta name="keywords" content={page.keywords.join(', ')} />}
        <link rel="canonical" href={`https://rental.shift-box.com/${page.page_type === 'calculator' ? 'tools' : page.page_type === 'guide' ? 'guides' : 'locations'}/${slug}`} />
      </Helmet>

      <AppLayout>
        <div className="max-w-4xl mx-auto space-y-12 py-8">
          {/* Hero Section */}
          <header className="text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">
              {page.hero_title}
            </h1>
            {page.hero_subtitle && (
              <p className="text-xl text-muted-foreground">
                {page.hero_subtitle}
              </p>
            )}
            <Button size="lg" asChild className="mt-4">
              <a href="/tariffs">
                Start Free <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
          </header>

          {/* Dynamic Sections */}
          <div className="space-y-8">
            {Array.isArray(page.sections) && page.sections.map((section: any, idx: number) => (
              <SeoSection key={idx} section={section} />
            ))}
          </div>

          {/* Footer CTA */}
          <footer className="text-center bg-muted rounded-lg p-8">
            <h2 className="text-2xl font-bold mb-4">Ready to get started?</h2>
            <p className="text-muted-foreground mb-6">
              Try rental.shift-box.com free for 30 days
            </p>
            <Button size="lg" asChild>
              <a href="/tariffs">Start Free</a>
            </Button>
          </footer>
        </div>
      </AppLayout>
    </>
  );
};

interface SeoSectionProps {
  section: {
    type: string;
    heading?: string;
    content?: string;
    items?: string[];
    buttonText?: string;
    buttonLink?: string;
  };
}

const SeoSection = ({ section }: SeoSectionProps) => {
  if (section.type === 'text') {
    // Sanitize HTML content to prevent XSS attacks
    const sanitizedContent = DOMPurify.sanitize(section.content || '', {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h3', 'h4', 'ul', 'ol', 'li', 'a'],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
      ALLOW_DATA_ATTR: false,
    });

    return (
      <section className="prose prose-lg max-w-none dark:prose-invert">
        {section.heading && (
          <h2 className="text-3xl font-bold mb-4 text-foreground">
            {section.heading}
          </h2>
        )}
        <div
          dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          className="text-muted-foreground"
        />
      </section>
    );
  }

  if (section.type === 'list') {
    return (
      <section>
        {section.heading && (
          <h2 className="text-3xl font-bold mb-4 text-foreground">
            {section.heading}
          </h2>
        )}
        <ul className="space-y-2 list-disc list-inside text-muted-foreground">
          {section.items?.map((item: string, i: number) => (
            <li key={i} className="text-lg">{item}</li>
          ))}
        </ul>
      </section>
    );
  }

  if (section.type === 'cta') {
    return (
      <section className="bg-primary/10 rounded-lg p-8 text-center my-8">
        {section.heading && (
          <h3 className="text-2xl font-bold mb-4 text-foreground">
            {section.heading}
          </h3>
        )}
        <Button size="lg" asChild>
          <a href={section.buttonLink || '/tariffs'}>
            {section.buttonText || 'Get Started'}
          </a>
        </Button>
      </section>
    );
  }

  return null;
};

export default SeoPage;
